import './App.css';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from './logo.png';
import LanguageSwitcher from './components/LanguageSwitcher';
import LogoTitle from './assets/LogoTitleKittyHaven.png';
import solveImage from './assets/sectionGames/solve_logic_puzzles.PNG';
import decorateImage from './assets/sectionGames/create_your_haven.PNG';
import squeakImage from './assets/sectionGames/unlock_new_cats.PNG';
import jordanImage from './assets/sectionAbout/jordanYerbe.jpg';
import tayeImage from './assets/sectionAbout/tayeCimon.jpg';
import KumaImage from './assets/sectionAbout/Kuma.jpg';
import YoshiImage from './assets/sectionAbout/Yoshi.jpeg';
import contactImage from './assets/sectionContact/contact.jpg';
import SteamIcon from './assets/social-media/steam.js';
import TwitterIcon from './assets/social-media/twitter.js';
import TikTokIcon from './assets/social-media/tiktok.js';
import BlueskyIcon from './assets/social-media/bluesky.js';
import bouton_steam from './assets/steam_logo.png';
 

function App() { 
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [steamModalOpen, setSteamModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [newsletterModalOpen, setNewsletterModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState(''); // 'success' ou 'error'
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [emailValidation, setEmailValidation] = useState({ valid: null, message: '' });
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleSteamModal = () => {
    setSteamModalOpen(!steamModalOpen);
  };

  const toggleNewsletterModal = () => {
    setNewsletterModalOpen(!newsletterModalOpen);
  };

  // Fonction de validation d'email avec sécurité anti-spam
  const validateEmail = (email) => {
    // Vérification basique de présence
    if (!email || typeof email !== 'string') {
      return { valid: false, message: t('newsletter.validation.required') };
    }

    // Vérification de la longueur (max 254 caractères selon RFC)
    if (email.length > 254) {
      return { valid: false, message: t('newsletter.validation.too_long') };
    }

    // Regex robuste pour validation du format email
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: t('newsletter.validation.invalid_format') };
    }

    // Liste des domaines temporaires/jetables courants
    const disposableDomains = [
      '10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com',
      'yopmail.com', 'temp-mail.org', 'getairmail.com', 'maildrop.cc',
      'throwaway.email', 'getnada.com', 'tempail.com', 'dispostable.com',
      'trashmail.com', '33mail.com', 'fakemailgenerator.com', 'mohmal.com'
    ];

    const domain = email.split('@')[1]?.toLowerCase();
    if (disposableDomains.includes(domain)) {
      return { valid: false, message: t('newsletter.validation.disposable_email') };
    }

    // Vérification des patterns de spam
    const spamPatterns = [
      /^[a-z]+[0-9]{3,}@/i, // lettres suivies de beaucoup de chiffres
      /^[0-9]{5,}@/i, // que des chiffres en début
      /^[a-z]{20,}@/i, // trop de lettres consécutives
      /[0-9]{10,}/, // trop de chiffres
      /(.)\1{4,}/, // caractères répétés 5 fois ou plus
      /^(test|spam|fake|temp|noreply|admin|webmaster|info)@/i // mots-clés suspects
    ];

    for (const pattern of spamPatterns) {
      if (pattern.test(email)) {
        return { valid: false, message: t('newsletter.validation.suspicious_email') };
      }
    }

    // Vérification de caractères suspects
    if (/[<>'"\\]/.test(email)) {
      return { valid: false, message: t('newsletter.validation.invalid_characters') };
    }

    // Vérification que le domaine contient au moins un point
    if (!domain || !domain.includes('.')) {
      return { valid: false, message: t('newsletter.validation.invalid_domain') };
    }

    // Vérification que le domaine n'est pas trop court
    if (domain.length < 4) {
      return { valid: false, message: t('newsletter.validation.domain_too_short') };
    }

    return { valid: true, message: '' };
  };

  // Fonction pour gérer les changements d'email avec validation en temps réel
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Validation en temps réel (avec un délai pour éviter trop d'exécutions)
    if (newEmail.length > 0) {
      const validation = validateEmail(newEmail);
      setEmailValidation(validation);
    } else {
      setEmailValidation({ valid: null, message: '' });
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Protection contre le spam : limitation de fréquence (1 tentative par minute)
    const currentTime = Date.now();
    const timeSinceLastSubmission = currentTime - lastSubmissionTime;
    const minInterval = 60000; // 1 minute en millisecondes
    
    if (timeSinceLastSubmission < minInterval && lastSubmissionTime > 0) {
      const remainingTime = Math.ceil((minInterval - timeSinceLastSubmission) / 1000);
      setModalMessage(t('newsletter.validation.rate_limit', { seconds: remainingTime }));
      setModalType('error');
      setNewsletterModalOpen(true);
      return;
    }
    
    // Validation complète de l'email
    const validation = validateEmail(email);
    if (!validation.valid) {
      setModalMessage(validation.message);
      setModalType('error');
      setNewsletterModalOpen(true);
      return;
    }

    setLastSubmissionTime(currentTime);
    setIsSubscribing(true);

    try {
      const response = await fetch('https://95vc0uedpj.execute-api.us-east-1.amazonaws.com/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setModalMessage(t('newsletter.success_message'));
        setModalType('success');
        setEmail(''); // Réinitialiser le champ email
      } else {
        setModalMessage('Une erreur est survenue lors de l\'inscription. Veuillez réessayer plus tard.');
        setModalType('error');
      }
    } catch (error) {
      setModalMessage('Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.');
      setModalType('error');
    } finally {
      setIsSubscribing(false);
      setNewsletterModalOpen(true);
    }
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          {/* Logo */}
          <div className="logo">
            <img src={logo} alt="logo" className='logo-image' />
          </div>

          {/* Menu hamburger pour mobile */}
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Navigation */}
          <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="#games" className="nav-button games-button">{t('navigation.games')}</a>
            <a href="#about" className="nav-button">{t('navigation.about')}</a>
            <a href="#contact" className="nav-button">{t('navigation.contact')}</a>
            <a href="/presskit/kitty-haven" className="nav-button" target="_blank" rel="noopener noreferrer">{t('navigation.presskit')}</a>
            <LanguageSwitcher />
          </nav>

          {/* Réseaux sociaux */}
          <div className="social-media">
            <a href="https://www.tiktok.com/@mabouya.studio" target="_blank" rel="noopener noreferrer" className="social-link social-button">
              <TikTokIcon/>
            </a>
            <a href="https://bsky.app/profile/mabouyastudio.bsky.social" target="_blank" rel="noopener noreferrer" className="social-link social-button">
              <BlueskyIcon/>
            </a>
            <a href="https://x.com/MabouyaStudio" target="_blank" rel="noopener noreferrer" className="social-link social-button">
              <TwitterIcon/>
            </a>
            <a href="https://store.steampowered.com/app/3930380/Kitty_Haven/" target="_blank" rel="noopener noreferrer" className="social-link social-button">
              <SteamIcon/>
            </a>
          </div>
        </div>
      </header>
      
      {/* Section Bannière */}
      <section className="banner-section" id="games">
        <div className="banner-content">
          <img src={LogoTitle} alt="Kitty Haven Logo" className="banner-image" />
          <div className="game-description">
            <p className="description-text">
              {t('banner.description')}
            </p>
            <div className="banner-buttons">
              <a href="https://store.steampowered.com/app/3930380/Kitty_Haven/" className="banner-btn primary" target="_blank" rel="noopener noreferrer">
                <img src={bouton_steam} alt="Steam" className="steam-icon" />
                <span style={{fontSize: '24px'}}>
                  {t('banner.subscribe_button')}
                  <br/>
                  <span style={{fontSize: '38px', fontWeight: 'bold'}}>
                  {t('banner.steam_button')}
                  <span style={{fontSize: '12px', fontWeight: 'bold',textTransform: 'uppercase',top: '-18px',position: 'relative'}}>
                    {t('banner.symbol_steam')}
                  </span>
                  </span> 
                </span>
              </a>
            </div>

          </div>
        </div>
      </section>
      
      {/* Section Games */}
      <section className="games-section" >
        <div className="games-container">
          <div className="game-card">
            <div className="game-image">
              <img src={solveImage} alt="Solve logic puzzles" />
            </div>
            <div className="game-content">
              <h3 className="game-title">{t('games.solve_puzzles.title')}</h3>
              <p className="game-description">
                {t('games.solve_puzzles.description')}
              </p>
            </div>
          </div>

          <div className="game-card">
            <div className="game-image">
              <img src={squeakImage} alt="Unlock new cats" />
            </div>

            <div className="game-content">
              <h3 className="game-title">{t('games.unlock_cats.title')}</h3>
              <p className="game-description">
                {t('games.unlock_cats.description')}
              </p>
            </div>
          </div>

          <div className="game-card">
            <div className="game-image">
              <img src={decorateImage} alt="Create your haven" />
            </div>
            <div className="game-content">
              <h3 className="game-title">{t('games.create_haven.title')}</h3>
              <p className="game-description">
                {t('games.create_haven.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section About Us */}
      <section className="about-section" id="about">
        <div className="about-container">
          <h2 className="about-title">{t('about.title')}</h2>
          <div className="about-content">
            <div className="developer-profiles">
              <div className="developer-card">
                <div className="developer-image">
                  <img src={tayeImage} alt="Taye Cimon" />
                </div>
                <h3 className="developer-name">Taye Cimon</h3>
              </div>
              <div className="developer-card">
                <div className="developer-image">
                  <img src={jordanImage} alt="Jordan Yerbe" />
                </div>
                <h3 className="developer-name">Jordan Yerbe</h3>
              </div>
              


              <div className="developer-card">
                <div className="developer-image">
                  <img src={KumaImage} alt="Kuma" />
                </div>
                <h3 className="developer-name">Kuma</h3>
              </div>

              <div className="developer-card">
                <div className="developer-image">
                  <img src={YoshiImage} alt="Yoshi" />
                </div>
                <h3 className="developer-name">Yoshi</h3>
              </div>
            </div>
            
            <div className="about-description">
              <p>{t('about.description')}</p>
            </div>
          </div>
        </div>
      </section>



      {/* Section Newsletter */}
      <section className="newsletter-section" id="newsletter">
        <div className="newsletter-container">
          <h2 className="newsletter-title">{t('newsletter.title')}</h2>
          
          <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
            
            <div className="input-wrapper">
              <input 
                type="email" 
                placeholder={t('newsletter.email_placeholder')} 
                className={`newsletter-input ${
                  emailValidation.valid === true ? 'valid' : 
                  emailValidation.valid === false ? 'invalid' : ''
                }`}
                value={email}
                onChange={handleEmailChange}
                disabled={isSubscribing}
              />
              {emailValidation.valid === true && (
                <span className="validation-icon valid">✓</span>
              )}
              {emailValidation.valid === false && (
                <span className="validation-icon invalid">✗</span>
              )}
            </div>

            <button 
              type="submit" 
              className={`newsletter-btn ${isSubscribing ? 'loading' : ''} ${
                emailValidation.valid === false ? 'disabled' : ''
              }`}
              disabled={isSubscribing || emailValidation.valid === false}
            >
              {isSubscribing ? t('newsletter.subscribing') : t('newsletter.subscribe_button')}
            </button>
          </form>
          {emailValidation.valid === false && emailValidation.message && (
              <div className="validation-message error">
                {emailValidation.message}
              </div>
            )}
        </div>
      </section>

      {/* Section Contact */}
      <section className="contact-section" id="contact">
        <div className="contact-container">
          <h2 className="contact-title">{t('contact.title')}</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-email">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="email-icon">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <a href="mailto:mabouyastudios@gmail.com" className="contact-email-link">
                  mabouyastudios@gmail.com
                </a>
              </div>
            </div>
            <div className="contact-image">
              <img src={contactImage} alt="Contact" />
            </div>
          </div>
        </div>
      </section>

      {/* Modal Steam */}
      {steamModalOpen && (
        <div className="modal-overlay" onClick={toggleSteamModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('modals.steam.title')}</h2>
              <button className="modal-close" onClick={toggleSteamModal}>
              <SteamIcon/>
              </button>
            </div>
            <div className="modal-body">
              <p>{t('modals.steam.message')}</p>
              <p>{t('modals.steam.stay_tuned')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Newsletter */}
      {newsletterModalOpen && (
        <div className="modal-overlay" onClick={toggleNewsletterModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalType === 'success' ? t('newsletter.success_title') : t('newsletter.error_title')}</h2>
              <button className="modal-close" onClick={toggleNewsletterModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className={`modal-message ${modalType}`}>{modalMessage}</p>
              <div className="modal-actions">
                <button className="modal-btn" onClick={toggleNewsletterModal}>
                  {modalType === 'success' ? t('newsletter.perfect_button') : t('newsletter.retry_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src={logo} alt="Mabouya Studios" className="footer-logo-image" />
          </div>
          <div className="footer-text">
            <p>{t('footer.copyright')}</p>
          </div>
          <div className="footer-social">
            <a href="https://www.tiktok.com/@mabouya.studio" target="_blank" rel="noopener noreferrer" className="footer-social-link social-button">
              <TikTokIcon/>
            </a>
            <a href="https://bsky.app/profile/mabouyastudio.bsky.social" target="_blank" rel="noopener noreferrer" className="footer-social-link social-button">
              <BlueskyIcon/>
            </a>
            <a href="https://x.com/MabouyaStudio" target="_blank" rel="noopener noreferrer" className="footer-social-link social-button">
              <TwitterIcon/>
            </a>
              <a href="https://store.steampowered.com/app/3930380/Kitty_Haven/" target="_blank" rel="noopener noreferrer" className="footer-social-link social-button">
              <SteamIcon/>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
export default App;
